import { useQuery } from '@tanstack/react-query';
import {
  Briefcase02,
  Building06,
  Calendar,
  CheckDone01,
  Dataflow03,
  File06,
  FileCheck02,
  Home03,
  Mail01,
  MessageChatCircle,
  PieChart03,
  Users01,
} from '@untitledui/icons';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { CollapseToggleButton } from '@/components/navigation/CollapseToggleButton';
import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useMobileNavHeader } from '@/hooks/useMobileNavHeader';
import { useNavCollapsed } from '@/hooks/useNavCollapsed';
import { useFeatureAccess } from '@/hooks/usePermissions';
import { usePortfolioAccess } from '@/hooks/usePortfolios';
import { MobileNavigationHeader } from '@/lib/application/app-navigation/base-components/mobile-header';
import { NavItemButton } from '@/lib/application/app-navigation/base-components/nav-item-button';
import { NavItemDropdownButton } from '@/lib/application/app-navigation/base-components/nav-item-dropdown-button';
import {
  COLLAPSED_WIDTH,
  EXPANDED_WIDTH,
} from '@/lib/application/app-navigation/sidebar-navigation-constants';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { entityTypeHasPipeline } from '@/lib/crm/hooks/useStages';
import { companyListConfig } from '@/lib/crm/list/config/companyListConfig';
import { contactListConfig } from '@/lib/crm/list/config/contactListConfig';
import { dealListConfig } from '@/lib/crm/list/config/dealListConfig';
import { noteListConfig } from '@/lib/crm/list/config/noteListConfig';
import { usePrefetchCRMList } from '@/lib/crm/list/hooks/usePrefetchCRMList';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons';
import { AhoyLogo } from '@/lib/foundations/logo/ahoy-logo';
import { AhoyLogoMinimal } from '@/lib/foundations/logo/ahoy-logo-minimal';
import { logger } from '@/lib/logger';
import { useMyPendingProposals } from '@/lib/proposals/hooks/useProposals';
import { useAnyChatRunning } from '@/lib/reasoning/realtime/useTrackedChatRun';
import { cx } from '@/utils/cx';

import { buildPipelineNavItem } from './buildPipelineNavItem';
import { NavList, type NavListItem } from './NavList';
import { SidebarUserFooter } from './SidebarUserFooter';
import type { NavShortcut } from './useNavShortcuts';
import { useNavShortcuts } from './useNavShortcuts';

/** System entity types that already have hardcoded nav items */
const SYSTEM_CRM_TYPES = new Set([
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
  'ahoy_note',
  'ahoy_task',
]);

/** Module-level sort/order constants so hook deps are stable (no new object per render). */
const CONTACT_PREFETCH_FILTERS = {
  sort: 'ahoy_created_at',
  order: 'desc',
} as const;
const COMPANY_PREFETCH_FILTERS = { sort: 'ahoy_name', order: 'asc' } as const;
const DEAL_PREFETCH_FILTERS = {
  sort: 'ahoy_created_at',
  order: 'desc',
} as const;
const NOTE_PREFETCH_FILTERS = {
  sort: 'ahoy_updated_at',
  order: 'desc',
} as const;

function formatBadgeCount(count: number): string {
  if (count > 99) {
    return '99+';
  }
  return String(count);
}

function formatChatRunStatus(count: number): string {
  if (count <= 0) {
    return 'No chat responses in progress';
  }
  if (count === 1) {
    return 'A chat response is in progress';
  }
  return `${count} chat responses in progress`;
}

function formatChatRunAriaSuffix(count: number): string | undefined {
  if (count <= 0) {
    return undefined;
  }
  if (count === 1) {
    return 'response in progress';
  }
  return `${count} responses in progress`;
}

interface SideNavigationProps {
  /** Hide the desktop sidebar (lg+). Used when another sidebar (e.g. Settings) occupies the slot. Mobile header still renders. */
  hideDesktopSidebar?: boolean;
}

export default function SideNavigation({
  hideDesktopSidebar = false,
}: SideNavigationProps) {
  const { orgId } = useParams();
  const { currentOrg } = useOrg();
  const location = useLocation();
  const { showMobileHeader } = useMobileNavHeader();

  // Seed the collapsed state synchronously from the persisted per-org
  // localStorage value (via useNavCollapsed) so it renders correctly on the
  // first paint — no auto-collapse, and no false→true flip after mount. The
  // sidebar never collapses on its own; it only reflects the user's last
  // manual toggle for this org.
  const { isCollapsed, setCollapsed } = useNavCollapsed(orgId);

  const { hasFeature } = useFeatureAccess();
  const hasChatFeature = hasFeature('chat');
  const hasAiActionsFeature = hasFeature('ai_actions');

  const { data: proposalsData } = useMyPendingProposals(
    parseInt(orgId || '0'),
    { staleTime: 60_000, enabled: !!orgId && hasAiActionsFeature }
  );

  // Derive unread count from the cached list: results where read_at === null.
  // Wrapping in useMemo prevents the filter from re-running on every render
  // (proposalsData may hold up to PROPOSALS_PAGE_SIZE=500 entries).
  const unreadCount = useMemo(() => {
    const results = proposalsData?.results;
    if (!results) {
      return 0;
    }
    return results.filter(p => p.read_at === null).length;
  }, [proposalsData?.results]);

  const [pulseKey, setPulseKey] = useState(0);
  const prevUnreadRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    // Reset on org change so switching orgs cannot trigger a stale pulse.
    prevUnreadRef.current = undefined;
  }, [orgId]);
  useEffect(() => {
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = unreadCount;
    if (prev === undefined) {
      // First observation for this org — do not pulse.
      return;
    }
    if (unreadCount > prev) {
      setPulseKey(k => k + 1);
    }
  }, [unreadCount]);

  // Chat run count — global/session-local (not org-derived), so unlike the
  // AI Actions unread count above, there is no org-reset effect: an org
  // switch does not change which chat runs are in flight.
  const chatRunCount = useAnyChatRunning();
  const [chatPulseKey, setChatPulseKey] = useState(0);
  const prevChatCountRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const prev = prevChatCountRef.current;
    prevChatCountRef.current = chatRunCount;
    if (prev === undefined) {
      // First observation — do not pulse.
      return;
    }
    if (chatRunCount > prev) {
      setChatPulseKey(k => k + 1);
    }
  }, [chatRunCount]);

  // Feature flags
  const hasPortfolioAccess = usePortfolioAccess();
  const isEmailThreadsEnabled = useFeatureFlag('email_threads');
  const isMeetingSummariesNavEnabled = useFeatureFlag('meeting_summaries_nav');
  // Same flag that gates the conversational builder: automations get their own
  // nav entry as part of that rollout, not independently of it.
  const isBuilderModeEnabled = useFeatureFlag('builder_mode');

  const orgIdNum = orgId ? parseInt(orgId) : undefined;

  // Always fetch pipelines in parallel with feature flags to eliminate waterfall.
  const {
    pipelines: allPipelines,
    isLoading: isLoadingPipelines,
    error,
  } = usePipelines(orgIdNum);

  useEffect(() => {
    if (!error) {
      return;
    }
    logger.error('Failed to load pipelines', error, {
      component: 'SideNavigation',
      orgId: orgIdNum,
    });
  }, [error, orgIdNum]);

  const contactsPrefetch = usePrefetchCRMList(
    orgIdNum,
    'ahoy_contact',
    CONTACT_PREFETCH_FILTERS,
    contactListConfig.columns
  );
  const companiesPrefetch = usePrefetchCRMList(
    orgIdNum,
    'ahoy_company',
    COMPANY_PREFETCH_FILTERS,
    companyListConfig.columns
  );
  const dealsPrefetch = usePrefetchCRMList(
    orgIdNum,
    'ahoy_deal',
    DEAL_PREFETCH_FILTERS,
    dealListConfig.columns
  );
  const notesPrefetch = usePrefetchCRMList(
    orgIdNum,
    'ahoy_note',
    NOTE_PREFETCH_FILTERS,
    noteListConfig.columns
  );

  // Check if meeting summaries exist (lightweight: limit 1, no properties)
  const { data: hasMeetingSummaries = false } = useQuery({
    queryKey: ['meetingSummariesExist', orgIdNum],
    queryFn: async () => {
      if (!orgIdNum) {
        return false;
      }
      const client = createCRMObjectClient(orgIdNum, 'ahoy_meeting_notes');
      const result = await client.list({ limit: 1, properties: [] });
      return (result.count ?? 0) > 0;
    },
    enabled: !!orgIdNum,
    staleTime: 5 * 60_000,
  });

  // Check if email threads exist — gates the Email threads nav link so the
  // entry only shows for orgs with synced email activity. Skipped entirely
  // when the `email_threads` feature flag is off to avoid an unnecessary
  // API call.
  const { data: hasEmailThreads = false } = useQuery({
    queryKey: ['emailThreadsExist', orgIdNum],
    queryFn: async () => {
      if (!orgIdNum) {
        return false;
      }
      const client = createCRMObjectClient(orgIdNum, 'ahoy_email_thread');
      const result = await client.list({ limit: 1, properties: [] });
      return (result.count ?? 0) > 0;
    },
    enabled: !!orgIdNum && isEmailThreadsEnabled,
    staleTime: 5 * 60_000,
  });

  // Fetch custom entity types for CRM nav
  const { data: navCustomTypesData } = useObjectTypes(orgIdNum);

  const customEntityTypes = useMemo(
    () => navCustomTypesData?.items ?? [],
    [navCustomTypesData]
  );
  const navAllObjectTypes = useMemo(
    () => navCustomTypesData?.allObjectTypes ?? [],
    [navCustomTypesData]
  );

  // Entity types the current user can READ — any resolved permission entry for a
  // type implies read access (matches the backend's read check). CRM nav items
  // are gated on this so a user never sees a section they'd get a 403 opening.
  // Built from the stable currentOrg.permissions reference as a Set for O(1)
  // lookups across the many nav items below. (usePermissions() is memoized on
  // the same reference, so either approach keeps the navItems memo stable.)
  const readableEntityTypes = useMemo(() => {
    const perms = currentOrg?.permissions as
      | Array<{ entityTypeName?: string; entity_type_name?: string }>
      | undefined;
    const set = new Set<string>();
    for (const p of perms ?? []) {
      const name = p.entityTypeName ?? p.entity_type_name;
      if (name) {
        set.add(name);
      }
    }
    return set;
  }, [currentOrg?.permissions]);

  const pipelineableTypeNames = useMemo(() => {
    const set = new Set<string>();
    for (const t of customEntityTypes) {
      if (entityTypeHasPipeline(t.entity_type_name, navAllObjectTypes)) {
        set.add(t.entity_type_name);
      }
    }
    return set;
  }, [customEntityTypes, navAllObjectTypes]);

  // Compute deal pipelines for navigation
  const dealPipelines = useMemo(
    () =>
      allPipelines.filter(
        p =>
          p.entity_type_name === 'ahoy_deal_pipeline' ||
          p.entity_type_name === 'ahoy_deal'
      ),
    [allPipelines]
  );

  // Task pipelines for the Tasks nav item.
  const taskPipelines = useMemo(
    () =>
      allPipelines.filter(
        p =>
          p.entity_type_name === 'ahoy_task' ||
          p.entity_type_name === 'ahoy_task_pipeline'
      ),
    [allPipelines]
  );

  // Compute custom entity pipelines (exclude system CRM pipelines: deals + notes + tasks)
  const customEntityPipelines = useMemo(
    () =>
      allPipelines.filter(
        p =>
          p.entity_type_name !== 'ahoy_deal_pipeline' &&
          p.entity_type_name !== 'ahoy_deal' &&
          p.entity_type_name !== 'ahoy_note' &&
          p.entity_type_name !== 'ahoy_task_pipeline' &&
          p.entity_type_name !== 'ahoy_task' &&
          !SYSTEM_CRM_TYPES.has(p.entity_type_name)
      ),
    [allPipelines]
  );

  const sidebarWidth = isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  useLayoutEffect(() => {
    if (hideDesktopSidebar) {
      return;
    }
    document.documentElement.style.setProperty(
      '--sidebar-width',
      `${sidebarWidth}px`
    );
    return () => {
      document.documentElement.style.removeProperty('--sidebar-width');
    };
  }, [sidebarWidth, hideDesktopSidebar]);

  // Build shortcuts map for useNavShortcuts
  const shortcuts = useMemo(() => {
    if (!orgId) {
      return {};
    }
    const map: Record<string, NavShortcut> = {
      home: { key: 'h', path: '' },
      chat: { key: 'c', path: 'conversations' },
      actions: { key: 'a', path: 'actions' },
      contacts: { key: 'o', path: 'crm/contacts' },
      companies: { key: 'm', path: 'crm/companies' },
      calendar: { key: 'l', path: 'meetings' },
      reports: { key: 'r', path: 'reporting' },
    };
    // 'u' rather than 'a' or 'm' — both are already taken (actions,
    // companies). Registered only when the nav entry exists, so the shortcut
    // never navigates somewhere the user has no way back from.
    if (isBuilderModeEnabled) {
      map.automations = { key: 'u', path: 'automations' };
    }
    return map;
  }, [orgId, isBuilderModeEnabled]);

  const { isModifierHeld } = useNavShortcuts(shortcuts, orgId || '');

  // Build nav items
  const navItems: NavListItem[] = useMemo(() => {
    if (!orgId) {
      return [];
    }

    // Every readable custom entity type surfaces in the nav, grouped by
    // ontology kind: party groups with the parties; asset and other kinds
    // (activity, legacy default-only, empty/dirty kind arrays) render above
    // Deals; case groups with Deals. All gated on read permission. Each
    // renders as a pipeline nav item when it has a pipeline, otherwise a
    // simple link.
    const buildCustomNavItem = (
      t: (typeof customEntityTypes)[number]
    ): NavListItem => {
      const icon = getEntityTypeIconComponent(
        t.entity_type_name,
        t.icon || undefined,
        t.entity_kind
      );
      const label = t.label_plural || t.label || t.entity_type_name;
      if (pipelineableTypeNames.has(t.entity_type_name)) {
        const entityPipelines = customEntityPipelines.filter(
          p =>
            !!p.base_entity_type_name &&
            p.base_entity_type_name === t.entity_type_name
        );
        return buildPipelineNavItem({
          orgId,
          entitySlug: t.slug,
          label,
          icon,
          pipelines: !isLoadingPipelines && !error ? entityPipelines : [],
        });
      }
      return { label, href: `/${orgId}/crm/${t.slug}`, icon };
    };
    // Tasks — one ACTIVITY item linking to the ahoy_task pipeline(s) under
    // the `ahoy-task` slug. With multiple pipelines, one child per pipeline
    // (default first, then A→Z — buildPipelineNavItem's sort) and clicking
    // the parent opens the FIRST pipeline rather than a list view. With a
    // single pipeline, buildPipelineNavItem renders no child level at all
    // and links the item straight to that pipeline. Never hidden while the
    // type is readable: if pipelines are still loading (or none exist yet)
    // the item falls back to the type's PIPELINE view — never the table —
    // so users can always get in and add tasks there.
    const tasksSlug = 'ahoy-task';
    const loadedTaskPipelines =
      !isLoadingPipelines && !error ? taskPipelines : [];
    const tasksBase = buildPipelineNavItem({
      orgId,
      entitySlug: tasksSlug,
      label: 'Tasks',
      icon: FileCheck02,
      pipelines: loadedTaskPipelines,
    });
    const tasksItem: NavListItem = {
      ...tasksBase,
      href:
        tasksBase.children?.[0]?.href ??
        (loadedTaskPipelines.length > 0
          ? tasksBase.href
          : `/${orgId}/crm/${tasksSlug}?view=pipeline`),
    };

    const readableCustomTypes = customEntityTypes.filter(t =>
      readableEntityTypes.has(t.entity_type_name)
    );
    const partyCustomTypes: typeof customEntityTypes = [];
    const assetCustomTypes: typeof customEntityTypes = [];
    const caseCustomTypes: typeof customEntityTypes = [];
    const otherCustomTypes: typeof customEntityTypes = [];
    for (const t of readableCustomTypes) {
      const kind = t.entity_kind || [];
      if (kind.includes('case')) {
        caseCustomTypes.push(t);
      } else if (kind.includes('asset')) {
        assetCustomTypes.push(t);
      } else if (kind.includes('party')) {
        partyCustomTypes.push(t);
      } else {
        otherCustomTypes.push(t);
      }
    }

    // Whether any CRM entity item is visible to this user — drives the "CRM"
    // section header so it's never shown above an empty section.
    const showCrmSection =
      readableEntityTypes.has('ahoy_contact') ||
      readableEntityTypes.has('ahoy_company') ||
      readableEntityTypes.has('ahoy_deal') ||
      (isEmailThreadsEnabled &&
        hasEmailThreads &&
        readableEntityTypes.has('ahoy_email_thread')) ||
      partyCustomTypes.length > 0 ||
      assetCustomTypes.length > 0 ||
      caseCustomTypes.length > 0 ||
      otherCustomTypes.length > 0;

    return [
      {
        label: 'Home',
        href: `/${orgId}`,
        icon: Home03,
        shortcut: 'Ctrl+⌘+H',
      },
      // Portfolio sits with Home (cross-org admin surface, not a CRM entity).
      // Org-scoped href so the page renders inside the app shell.
      ...(hasPortfolioAccess === true
        ? [
            {
              label: 'Portfolio',
              href: `/${orgId}/portfolio`,
              icon: Briefcase02,
            } as NavListItem,
          ]
        : []),
      {
        label: 'Chat',
        href: `/${orgId}/conversations`,
        icon: MessageChatCircle,
        shortcut: 'Ctrl+⌘+C',
        badge:
          hasChatFeature && chatRunCount > 0
            ? formatBadgeCount(chatRunCount)
            : undefined,
        pulseKey: hasChatFeature ? chatPulseKey : undefined,
        badgeDataComponent: 'chat-run-badge',
        locked: !hasChatFeature,
      },
      {
        label: 'AI Actions',
        href: `/${orgId}/actions`,
        icon: CheckDone01,
        badge:
          hasAiActionsFeature && unreadCount > 0
            ? formatBadgeCount(unreadCount)
            : undefined,
        pulseKey: hasAiActionsFeature ? pulseKey : undefined,
        shortcut: 'Ctrl+⌘+A',
        locked: !hasAiActionsFeature,
      },
      // CRM section — every entity item is gated on the user's READ permission
      // for that type (readableEntityTypes). The section header is dropped too
      // when nothing under it is visible, so a user with no CRM read access
      // never sees an empty "CRM" heading.
      ...(showCrmSection ? [{ label: 'CRM', isSectionHeader: true }] : []),
      ...(readableEntityTypes.has('ahoy_contact')
        ? [
            {
              label: 'Contacts',
              href: `/${orgId}/crm/contacts`,
              icon: Users01,
              shortcut: 'Ctrl+⌘+O',
              onPrefetch: contactsPrefetch,
            } as NavListItem,
          ]
        : []),
      ...(readableEntityTypes.has('ahoy_company')
        ? [
            {
              label: 'Companies',
              href: `/${orgId}/crm/companies`,
              icon: Building06,
              shortcut: 'Ctrl+⌘+M',
              onPrefetch: companiesPrefetch,
            } as NavListItem,
          ]
        : []),
      // Parties — party-kind custom entities, grouped with the parties.
      ...partyCustomTypes.map<NavListItem>(buildCustomNavItem),
      // Assets — asset-kind custom entities, grouped with the parties, above Deals.
      ...assetCustomTypes.map<NavListItem>(buildCustomNavItem),
      // Other — activity-kind, legacy default-only, or otherwise unclassified
      // custom entities. Grouped above Deals alongside assets.
      ...otherCustomTypes.map<NavListItem>(buildCustomNavItem),
      ...(readableEntityTypes.has('ahoy_deal')
        ? [
            buildPipelineNavItem({
              orgId,
              entitySlug: 'deals',
              label: 'Deals',
              icon: getEntityTypeIconComponent('ahoy_deal'),
              pipelines: !isLoadingPipelines && !error ? dealPipelines : [],
              onPrefetch: dealsPrefetch,
            }),
          ]
        : []),
      // Cases — case-kind custom entities, grouped with Deals.
      ...caseCustomTypes.map<NavListItem>(buildCustomNavItem),
      // Email threads — gated behind the `email_threads` feature flag and
      // only shown when the org has synced email activity.
      ...(isEmailThreadsEnabled &&
      hasEmailThreads &&
      readableEntityTypes.has('ahoy_email_thread')
        ? [
            {
              label: 'Email Threads',
              href: `/${orgId}/crm/email-threads`,
              icon: Mail01,
            } as NavListItem,
          ]
        : []),
      // Automation — its own section while gated behind `builder_mode`.
      // Header and item show/hide together so the header never sits above
      // an empty section.
      ...(isBuilderModeEnabled
        ? [
            { label: 'Automation', isSectionHeader: true } as NavListItem,
            {
              label: 'Automations',
              href: `/${orgId}/automations`,
              icon: Dataflow03,
              shortcut: 'Ctrl+⌘+U',
            } as NavListItem,
          ]
        : []),
      // Activity — Notes, Tasks, and Meetings. Always shown: Meetings is
      // ungated, so the header never sits above an empty section.
      { label: 'Activity', isSectionHeader: true },
      // Notes — plain link to the notes list. Gated only on read
      // permission for the type (the old `notes_nav` feature flag is gone).
      ...(readableEntityTypes.has('ahoy_note')
        ? [
            {
              label: 'Notes',
              href: `/${orgId}/crm/ahoy-note?view=table`,
              icon: getEntityTypeIconComponent('ahoy_note'),
              onPrefetch: notesPrefetch,
            } as NavListItem,
          ]
        : []),
      // Tasks — sits directly under Notes. Gated on ahoy_task read permission.
      ...(readableEntityTypes.has('ahoy_task') ? [tasksItem] : []),
      {
        label: 'Meetings',
        href: `/${orgId}/meetings`,
        icon: Calendar,
        shortcut: 'Ctrl+⌘+L',
        children: [
          {
            label: 'Booking Links',
            href: `/${orgId}/crm/booking-types`,
            icon: Calendar,
          } as NavListItem,
          ...(isMeetingSummariesNavEnabled && hasMeetingSummaries
            ? [
                {
                  label: 'Meeting Summaries',
                  href: `/${orgId}/meeting-summaries`,
                  icon: File06,
                } as NavListItem,
              ]
            : []),
        ],
      },
      { label: '', isDivider: true },
      {
        label: 'Reports',
        href: `/${orgId}/reporting`,
        icon: PieChart03,
        shortcut: 'Ctrl+⌘+R',
      },
    ];
  }, [
    isBuilderModeEnabled,
    orgId,
    unreadCount,
    pulseKey,
    chatRunCount,
    chatPulseKey,
    contactsPrefetch,
    companiesPrefetch,
    dealsPrefetch,
    notesPrefetch,
    dealPipelines,
    taskPipelines,
    customEntityTypes,
    readableEntityTypes,
    hasMeetingSummaries,
    hasEmailThreads,
    customEntityPipelines,
    pipelineableTypeNames,
    isEmailThreadsEnabled,
    isMeetingSummariesNavEnabled,
    isLoadingPipelines,
    error,
    hasPortfolioAccess,
    hasChatFeature,
    hasAiActionsFeature,
  ]);

  // Flatten nav items for collapsed (icon-only) view
  const collapsedItems = useMemo(() => {
    if (!orgId) {
      return [];
    }
    const items: Array<{
      label: string;
      href?: string;
      icon: Parameters<typeof NavItemButton>[0]['icon'];
      dropdownItems?: Array<{ label: string; href: string }>;
      badge?: string | number;
      pulseKey?: number;
      ariaLabelSuffix?: string;
      badgeDataComponent?: string;
    }> = [];
    for (const item of navItems) {
      if (item.isSectionHeader || !item.icon) {
        continue;
      }
      if (item.children?.length) {
        const pipelineItems = item.children
          .filter((c): c is typeof c & { href: string } => !!c.href)
          .map(c => ({ label: c.label, href: c.href }));
        // "All {entities}" links to the table view at the top of the dropdown.
        // Skipped when the parent href IS the first child's href (Tasks — the
        // parent opens the first pipeline), where it would be a duplicate row.
        const allLink =
          item.href && item.href !== pipelineItems[0]?.href
            ? [{ label: `All ${item.label}`, href: item.href }]
            : [];
        items.push({
          label: item.label,
          icon: item.icon,
          dropdownItems: [...allLink, ...pipelineItems],
        });
      } else {
        let ariaLabelSuffix: string | undefined;
        if (item.label === 'AI Actions' && unreadCount > 0) {
          ariaLabelSuffix = `${unreadCount} unread`;
        } else if (item.label === 'Chat') {
          ariaLabelSuffix = formatChatRunAriaSuffix(chatRunCount);
        }
        items.push({
          label: item.label,
          href: item.href,
          icon: item.icon,
          badge: item.badge,
          pulseKey: item.pulseKey,
          ariaLabelSuffix,
          badgeDataComponent:
            item.label === 'Chat' ? 'chat-run-badge' : undefined,
        });
      }
    }
    return items;
  }, [navItems, orgId, unreadCount, chatRunCount]);

  if (!orgId) {
    return null;
  }

  // User footer. Lives in `SidebarUserFooter` so the settings sidebar can
  // reuse it; `key` remounts it across the collapse transition so an open menu
  // doesn't carry over. Only the desktop sidebar collapses — the mobile drawer
  // is full-width, so it always renders the expanded card with name/email.
  const userFooter = (
    <SidebarUserFooter
      key={isCollapsed ? 'collapsed' : 'expanded'}
      collapsed={isCollapsed}
    />
  );
  const mobileUserFooter = <SidebarUserFooter collapsed={false} />;

  const handleCollapseToggle = () => {
    const next = !isCollapsed;
    setCollapsed(next);
  };

  return (
    <>
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        aria-label='AI actions status'
        className='sr-only'
      >
        {unreadCount > 0
          ? `${unreadCount} unread AI ${unreadCount === 1 ? 'action' : 'actions'}`
          : 'No unread AI actions'}
      </div>
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        aria-label='Chat status'
        className='sr-only'
      >
        {formatChatRunStatus(chatRunCount)}
      </div>
      {/* Desktop sidebar navigation — hidden when another sidebar occupies the slot */}
      {!hideDesktopSidebar && (
        <aside
          style={{ width: sidebarWidth }}
          className='hidden lg:flex flex-col h-full fixed left-0 top-0 z-50 transition-all duration-200 bg-primary border-r border-secondary'
        >
          <div className='flex flex-col h-full bg-primary relative'>
            {/* Header with logo */}
            <div
              className={cx(
                'flex items-center h-[52px] mt-3',
                isCollapsed ? 'justify-center' : 'pl-[30px]'
              )}
            >
              {isCollapsed ? (
                <Link to={`/${orgId}`} aria-label='Go to homepage'>
                  <AhoyLogoMinimal className='size-7' />
                </Link>
              ) : (
                <AhoyLogo href={`/${orgId}`} size='sm' />
              )}
            </div>

            {/* Navigation */}
            <nav className='flex-1 overflow-y-auto mt-2'>
              {isCollapsed ? (
                <ul className='flex flex-col items-center gap-0.5 px-3'>
                  {collapsedItems.map(item => (
                    <li key={item.label}>
                      {item.dropdownItems ? (
                        <NavItemDropdownButton
                          label={item.label}
                          icon={item.icon}
                          items={item.dropdownItems}
                          current={item.dropdownItems.some(di => {
                            const basePath = di.href.split('?')[0];
                            return (
                              location.pathname === basePath ||
                              location.pathname.startsWith(basePath + '/')
                            );
                          })}
                          size='sm'
                        />
                      ) : (
                        <NavItemButton
                          size='sm'
                          current={
                            !!item.href &&
                            (item.href === `/${orgId}`
                              ? location.pathname === item.href
                              : location.pathname === item.href ||
                                location.pathname.startsWith(item.href + '/'))
                          }
                          href={item.href}
                          label={item.label}
                          icon={item.icon}
                          badge={item.badge}
                          pulseKey={item.pulseKey}
                          ariaLabelSuffix={item.ariaLabelSuffix}
                          badgeDataComponent={item.badgeDataComponent}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <NavList
                  items={navItems}
                  activeUrl={location.pathname}
                  isModifierHeld={isModifierHeld}
                  orgId={orgId}
                />
              )}
            </nav>

            {/* Collapse/expand button */}
            <div
              className={cx(
                'flex px-3 pb-2',
                isCollapsed ? 'justify-center' : 'justify-end'
              )}
            >
              <CollapseToggleButton
                collapsed={isCollapsed}
                onToggle={handleCollapseToggle}
                expandLabel='Expand sidebar'
                collapseLabel='Collapse sidebar'
              />
            </div>

            {/* Footer (user section) */}
            {isCollapsed ? (
              <div className='px-3 py-4 border-t border-secondary flex justify-center'>
                {userFooter}
              </div>
            ) : (
              <div className='px-4 pb-4 pt-2'>{userFooter}</div>
            )}
          </div>
        </aside>
      )}

      {/* Mobile header navigation */}
      {showMobileHeader && (
        <MobileNavigationHeader orgId={orgId}>
          <aside className='group flex h-full max-h-full w-full max-w-full flex-col justify-between overflow-y-auto bg-primary pt-4'>
            <div className='px-4'>
              <AhoyLogo href={`/${orgId}`} size='sm' />
            </div>

            <NavList
              items={navItems}
              activeUrl={location.pathname}
              isModifierHeld={isModifierHeld}
              orgId={orgId}
            />

            <div className='mt-auto px-3 py-4 border-t border-secondary'>
              {mobileUserFooter}
            </div>
          </aside>
        </MobileNavigationHeader>
      )}
    </>
  );
}
